import { makeStyles } from '@material-ui/core/styles'
import { isEmpty } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Actions from 'actions'
import useClinic from 'hooks/useClinic'
import usePlanLimits from 'hooks/usePlanLimits'
import { useCountry } from 'hooks/useCountry'
import BaseModal from '../../common/modals/BaseModal'
import OrderGrinKitsAmountContainer from './OrderGrinKitsAmountContainer'
import OrderGrinKitsCheckout from './OrderGrinKitsCheckout'
import OrderGrinKitsComparison from './OrderGrinKitsComparison'
import { isMobile } from 'utils/mobileUtils'
import { removeKitsOrder, setKistOrder, getKistOrder } from 'utils/storageUtils'
import {
  GRIN_SCOPE_MAX_IN_ORDER,
  GRIN_SCOPE_TYPE_LG,
  GRIN_SCOPE_TYPE_LG_MINI,
  GRIN_SCOPE_TYPE_MINI,
  GRIN_SCOPE_TYPE_REGULAR,
  SUBSCRIPTION_PLAN_GRIN_SCOPE_MAX_IN_ORDER,
  SUBSCRIPTION_PLAN_GRIN_SCOPE_MINI_MAX_IN_ORDER
} from 'consts/billingConsts'
import { COUNTRY_US, COUNTRY_IN } from 'consts/countryConsts'
import useScopesShippingFee from 'hooks/useScopesShippingFee'
import { trackEvent } from 'utils/analyticsUtils'
import useGaFlags from 'hooks/useGaFlags'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { getScopeProductsContent } from './scopeProducts'
import useScopeOrderValidation from './useScopeOrderValidation'
import { getOrderGrinKitsAmountStepProducts } from './orderGrinKitsUtils'
import { resolveScopeMaxQuantity } from 'utils/scopeOrderUtils'
import TrademarkText from 'components/common/TrademarkText'

const useStyles = makeStyles(theme => ({
  body: {
    minWidth: isMobile() ? 0 : 680,
    overflowX: 'hidden'
  },
  amountTitle: {
    padding: '20px 36px 8px !important',
    // BaseModal applies titleClassName to both the header and its text child.
    '& > div': {
      padding: '0 !important'
    }
  },
  amountContent: {
    paddingBottom: '12px !important'
  },
  amountActions: {
    paddingBottom: '20px !important'
  },
  comparisonBody: {
    minWidth: ({ scopeLGOrdering }) => (isMobile() ? 0 : scopeLGOrdering ? 1040 : 840),
    overflowX: 'hidden'
  },
  contentBody: {
    padding: '0 24px 40px 24px !important'
  },
  modalActions: {
    padding: '10px !important',
    flexDirection: 'column-reverse',
    '& > button': {
      marginLeft: '0 !important',
      marginTop: 10
    }
  },
  modalTitle: {
    paddingBottom: '5px !important',
    '& > div': {
      fontSize: '33px '
    }
  },
  confirmationStepBody: {
    minWidth: 540
  },
  horizontalSeparator: {
    marginBottom: 20,
    marginLeft: -24,
    marginRight: -24,
    height: '1px',
    borderTop: `1px solid ${theme.palette.grey[300]}`
  }
}))

const AMOUNT_STEP = 'AMOUNT_STEP'
const CONFIRMATION_STEP = 'CONFIRMATION_STEP'
const currency = '$'

const OrderGrinKitsModal = () => {
  const { scopeLGOrdering } = useFeatureFlags()
  const classes = useStyles({ scopeLGOrdering })
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { canClinicBuyScopes, hasFreeScopes } = useClinic()
  const gaFlags = useGaFlags()

  const doctorName = useSelector(state => state.profileReducer?.doctor?.name)

  const isOrderOpen = useSelector(state => state.billingReducer.order.isOrderGrinKitsModalOpen)
  const isSubmittingOrder = useSelector(state => state.billingReducer.order.isLoading)
  const isCalculatingTaxes = useSelector(state => state.billingReducer.order.isLoadingTaxes)
  const isLoadingScopeProduct = useSelector(state => state.productReducer.isLoadingScopeProducts)
  const practiceDetails = useSelector(state => state.practiceReducer.details)
  const productScope = useSelector(state => state.productReducer.scope.data)
  const productScopeMini = useSelector(state => state.productReducer.scopeMini.data)
  const productScopeLG = useSelector(state => state.productReducer.scopeLG.data)
  const productScopeLGMini = useSelector(state => state.productReducer.scopeLGMini.data)
  const productScopePlanGroup = useSelector(state => state.productReducer.scopePlanGroup)
  const loadedIncludeScopeLG = useSelector(state => state.productReducer.includesScopeLG)
  const maxQuantityLG = useSelector(state => state.productReducer.maxQuantityLG)
  const maxQuantityLGMini = useSelector(state => state.productReducer.maxQuantityLGMini)
  const shippingFee = useSelector(state => state.productReducer.shippingFee)
  const taxes = useSelector(state => state.billingReducer.order.taxAmount)
  const { coupon } = useSelector(state => state.billingReducer.order.coupon)
  const invoiceId = useSelector(state => state.billingReducer.order.invoiceId)
  const totalDiscount = useSelector(state => state.billingReducer.order.totalDiscount)
  const planLimits = usePlanLimits()
  const quantity = useSelector(state => state.billingReducer.order.grinKitsQuantity[GRIN_SCOPE_TYPE_REGULAR])
  const quantityMini = useSelector(state => state.billingReducer.order.grinKitsQuantity[GRIN_SCOPE_TYPE_MINI])
  const storedQuantityLG = useSelector(state => state.billingReducer.order.grinKitsQuantity[GRIN_SCOPE_TYPE_LG])
  const storedQuantityLGMini = useSelector(
    state => state.billingReducer.order.grinKitsQuantity[GRIN_SCOPE_TYPE_LG_MINI]
  )
  const practicePlanGroup = useSelector(state => state.practiceReducer?.billing?.grinPlan?.planGroup)
  const includeScopeLG = !!scopeLGOrdering
  // LG rows render only when the backend actually returned the product. This hides them for ortho practices,
  // when the Stripe product is not configured, and when the deployed backend predates LG mini support.
  const canOrderScopeLG = includeScopeLG && !isEmpty(productScopeLG)
  const canOrderScopeLGMini = includeScopeLG && !isEmpty(productScopeLGMini)
  // Draft quantities can survive a failed product fetch. Only orderable products reach checkout and requests.
  const quantityLG = canOrderScopeLG ? storedQuantityLG : 0
  const quantityLGMini = canOrderScopeLGMini ? storedQuantityLGMini : 0
  const [shippingAddress, setShippingAddress] = useState({})
  const [agreedTerms, setAgreedTerms] = useState(false)
  const [shouldShowErrors, setShouldShowErrors] = useState(false)
  const [step, setStep] = useState(AMOUNT_STEP)
  const [showProductComparison, setShowProductComparison] = useState(false)

  const { getStateKey } = useCountry(shippingAddress?.country)
  const { shippingFee: newShippingFee } = useScopesShippingFee(shippingAddress, {
    regular: quantity,
    mini: quantityMini,
    lg: canOrderScopeLG ? quantityLG : 0,
    lgMini: canOrderScopeLGMini ? quantityLGMini : 0
  })

  const grinKitMaxScopeQuantity = useMemo(
    () =>
      planLimits.isSubscriptionPlan && hasFreeScopes ? SUBSCRIPTION_PLAN_GRIN_SCOPE_MAX_IN_ORDER : planLimits.maxScopes,
    [planLimits.isSubscriptionPlan, planLimits.maxScopes, hasFreeScopes]
  )
  const grinKitMiniMaxScopeQuantity = useMemo(
    () =>
      planLimits.isSubscriptionPlan && hasFreeScopes
        ? SUBSCRIPTION_PLAN_GRIN_SCOPE_MINI_MAX_IN_ORDER
        : planLimits.maxScopes,
    [planLimits.isSubscriptionPlan, planLimits.maxScopes, hasFreeScopes]
  )
  // LG rows share the Regular row's plan-side limit (including the subscription free-scopes in-order cap),
  // narrowed by the backend cap. GRIN_SCOPE_MAX_IN_ORDER is the last resort when neither side is known.
  const grinKitLGMaxScopeQuantity = useMemo(
    () =>
      resolveScopeMaxQuantity({ backendMaxQuantity: maxQuantityLG, planMaxScopes: grinKitMaxScopeQuantity }) ??
      GRIN_SCOPE_MAX_IN_ORDER,
    [maxQuantityLG, grinKitMaxScopeQuantity]
  )
  const grinKitLGMiniMaxScopeQuantity = useMemo(
    () =>
      resolveScopeMaxQuantity({ backendMaxQuantity: maxQuantityLGMini, planMaxScopes: grinKitMaxScopeQuantity }) ??
      GRIN_SCOPE_MAX_IN_ORDER,
    [maxQuantityLGMini, grinKitMaxScopeQuantity]
  )
  const scopeInputs = [
    {
      key: 'quantity',
      type: GRIN_SCOPE_TYPE_REGULAR,
      quantity,
      maxQuantity: grinKitMaxScopeQuantity
    },
    {
      key: 'quantityMini',
      type: GRIN_SCOPE_TYPE_MINI,
      quantity: quantityMini,
      maxQuantity: grinKitMiniMaxScopeQuantity
    },
    ...(canOrderScopeLG
      ? [
          {
            key: 'quantityLG',
            type: GRIN_SCOPE_TYPE_LG,
            quantity: quantityLG,
            maxQuantity: grinKitLGMaxScopeQuantity
          }
        ]
      : []),
    ...(canOrderScopeLGMini
      ? [
          {
            key: 'quantityLGMini',
            type: GRIN_SCOPE_TYPE_LG_MINI,
            quantity: quantityLGMini,
            maxQuantity: grinKitLGMiniMaxScopeQuantity
          }
        ]
      : [])
  ]
  const { formErrors, validateForm, onQuantityChange, resetValidation } = useScopeOrderValidation(scopeInputs)

  const isNewShippingFee = useMemo(() => gaFlags?.newShippingFee, [gaFlags])
  const scopeProductsContent = useMemo(() => getScopeProductsContent(t), [t])
  const hasLoadedScopeProduct = !isEmpty(productScope)
  const scopePrice = productScope?.price || 0
  const scopePreviousPrice = productScope?.previousPrice || null
  const scopeMiniPrice = productScopeMini?.price || 0
  const scopeMiniPreviousPrice = productScopeMini?.previousPrice || null
  const scopeLGPrice = productScopeLG?.price || 0
  const scopeLGMiniPrice = productScopeLGMini?.price || 0

  const setQuantity = useCallback(
    value =>
      dispatch(
        Actions.setScopeQuantity({
          scopeType: GRIN_SCOPE_TYPE_REGULAR,
          value
        })
      ),
    [dispatch]
  )

  const setQuantityMini = useCallback(
    value =>
      dispatch(
        Actions.setScopeQuantity({
          scopeType: GRIN_SCOPE_TYPE_MINI,
          value
        })
      ),
    [dispatch]
  )

  const setQuantityLG = useCallback(
    value =>
      dispatch(
        Actions.setScopeQuantity({
          scopeType: GRIN_SCOPE_TYPE_LG,
          value
        })
      ),
    [dispatch]
  )

  const setQuantityLGMini = useCallback(
    value =>
      dispatch(
        Actions.setScopeQuantity({
          scopeType: GRIN_SCOPE_TYPE_LG_MINI,
          value
        })
      ),
    [dispatch]
  )

  const mappedShippingAddress = useMemo(() => {
    const address = {
      street: shippingAddress.address1,
      detailedAddress: shippingAddress.address2,
      city: shippingAddress.city,
      country: shippingAddress.country,
      state: getStateKey(shippingAddress.state, shippingAddress.country),
      zip: shippingAddress.zip
    }
    if (shippingAddress.country === COUNTRY_IN) {
      address.detailedAddress = shippingAddress.address2
    }
    return address
  }, [getStateKey, shippingAddress])

  const setInitialShippingAddress = useCallback(() => {
    setShippingAddress({
      address1: practiceDetails.address1,
      address2: practiceDetails.address2,
      city: practiceDetails.city,
      country: practiceDetails.country || COUNTRY_US,
      state: practiceDetails.state,
      zip: practiceDetails.zip
    })
  }, [practiceDetails, setShippingAddress])

  const resetFields = useCallback(() => {
    setQuantity(10)
    setQuantityMini(0)
    setQuantityLG(0)
    setQuantityLGMini(0)
    setAgreedTerms(false)
    setInitialShippingAddress()
    resetValidation()
    setShouldShowErrors(false)
    dispatch(Actions.resetCoupon({}))
  }, [
    setQuantity,
    setQuantityMini,
    setQuantityLG,
    setQuantityLGMini,
    setInitialShippingAddress,
    resetValidation,
    dispatch
  ])

  const calculateOrderTax = useCallback(() => {
    dispatch(
      Actions.calculateOrderTax({
        quantity,
        quantityMini,
        quantityLG,
        quantityLGMini,
        shippingAddress: mappedShippingAddress,
        grinPlanKey: planLimits.grinPlanKey
      })
    )
  }, [dispatch, quantity, quantityLG, quantityLGMini, quantityMini, mappedShippingAddress, planLimits.grinPlanKey])

  const handleNoPaymentOrder = useCallback(() => {
    setShouldShowErrors(true)
    if (!validateForm()) {
      return
    }

    dispatch(
      Actions.createOrderGrinKits({
        purchase: false,
        quantity,
        quantityMini,
        quantityLG,
        quantityLGMini,
        shippingAddress: mappedShippingAddress
      })
    )
  }, [validateForm, dispatch, quantity, quantityLG, quantityLGMini, quantityMini, mappedShippingAddress])

  const handleRemoveDraftKistOrder = useCallback(() => {
    if (isMobile()) {
      removeKitsOrder()
      dispatch(Actions.setHasNotificationsIndicator(false))
    }
  }, [dispatch])

  const handleSetDraftKitsOrder = useCallback(() => {
    if (isMobile()) {
      dispatch(Actions.setHasNotificationsIndicator(true))
      setKistOrder({
        quantity,
        quantityMini,
        quantityLG,
        quantityLGMini
      })
    }
  }, [dispatch, quantity, quantityLG, quantityLGMini, quantityMini])

  const handlePayment = useCallback(() => {
    if (isSubmittingOrder) {
      return
    }

    setShouldShowErrors(true)
    if (!validateForm()) {
      return
    }

    if (!agreedTerms) {
      return
    }

    handleRemoveDraftKistOrder()
    trackEvent('Order grin scopes - scopes ordered', {
      regularScopesAmount: quantity,
      miniScopesAmoumt: quantityMini,
      lgScopesAmount: quantityLG,
      lgMiniScopesAmount: quantityLGMini
    })
    dispatch(
      Actions.createOrderGrinKits({
        purchase: true,
        invoiceId,
        shippingAddress
      })
    )
  }, [
    isSubmittingOrder,
    validateForm,
    agreedTerms,
    handleRemoveDraftKistOrder,
    quantity,
    quantityLG,
    quantityLGMini,
    quantityMini,
    dispatch,
    invoiceId,
    shippingAddress
  ])

  const resetCoupon = useCallback(() => dispatch(Actions.clearCouponCode()), [dispatch])

  const handleClose = useCallback(() => {
    dispatch(Actions.setOrderGrinKitsModalVisibility(false))
    handleRemoveDraftKistOrder()
    setStep(AMOUNT_STEP)
    resetFields()
  }, [dispatch, resetFields, handleRemoveDraftKistOrder])

  const handleClickSecondaryOnConfirmStep = useCallback(() => {
    dispatch(Actions.resetCoupon({}))
    setStep(AMOUNT_STEP)

    if (invoiceId) {
      dispatch(Actions.deleteInvoice({ invoiceId }))
    }
  }, [dispatch, invoiceId])

  const handleSubmitAmountStep = useCallback(
    e => {
      e.preventDefault()
      if (!validateForm()) {
        return
      }

      if (!canClinicBuyScopes()) {
        handleNoPaymentOrder()
      } else {
        handleSetDraftKitsOrder()
        setStep(CONFIRMATION_STEP)
      }
    },
    [validateForm, canClinicBuyScopes, handleNoPaymentOrder, handleSetDraftKitsOrder]
  )

  const steps = useMemo(
    () => ({
      [AMOUNT_STEP]: {
        primaryLabel: t('general.continue'),
        handleSubmit: handleSubmitAmountStep,
        secondaryLabel: !isMobile() && t('general.cancel'),
        handleSecondary: handleClose,
        title: t('dialogs.orderScopes.title')
      },
      [CONFIRMATION_STEP]: {
        primaryLabel: isMobile() ? t('dialogs.orderScopes.purchaseButton') : t('general.confirm'),
        handleSubmit: () => {},
        secondaryLabel: t('general.back'),
        handleSecondary: handleClickSecondaryOnConfirmStep,
        title: isMobile() ? t('dialogs.orderScopes.confirmPayment') : t('dialogs.orderScopes.title')
      }
    }),
    [t, handleSubmitAmountStep, handleClose, handleClickSecondaryOnConfirmStep]
  )

  const handleShippingAddressChange = useCallback(
    address => {
      trackEvent('Order scopes modal - shipping address changed', {
        currentAddress: shippingAddress,
        newAddress: address,
        doctorName
      })
      setShippingAddress(address)
      isNewShippingFee && handleSetDraftKitsOrder()
    },
    [handleSetDraftKitsOrder, isNewShippingFee, shippingAddress, doctorName]
  )

  const products = useMemo(
    () => [
      {
        ...scopeProductsContent[GRIN_SCOPE_TYPE_REGULAR],
        description: scopeProductsContent[GRIN_SCOPE_TYPE_REGULAR].selectorDescription,
        price: scopePrice,
        previousPrice: scopePreviousPrice,
        quantity,
        maxQuantity: grinKitMaxScopeQuantity,
        onSetQuantity: setQuantity,
        error: formErrors?.quantity
      },
      {
        ...scopeProductsContent[GRIN_SCOPE_TYPE_MINI],
        description: scopeProductsContent[GRIN_SCOPE_TYPE_MINI].selectorDescription,
        price: scopeMiniPrice,
        previousPrice: scopeMiniPreviousPrice,
        quantity: quantityMini,
        maxQuantity: grinKitMiniMaxScopeQuantity,
        onSetQuantity: setQuantityMini,
        error: formErrors?.quantityMini
      },
      ...(canOrderScopeLG
        ? [
            {
              ...scopeProductsContent[GRIN_SCOPE_TYPE_LG],
              description: scopeProductsContent[GRIN_SCOPE_TYPE_LG].selectorDescription,
              price: scopeLGPrice,
              quantity: quantityLG,
              maxQuantity: grinKitLGMaxScopeQuantity,
              onSetQuantity: setQuantityLG,
              error: formErrors?.quantityLG
            }
          ]
        : []),
      ...(canOrderScopeLGMini
        ? [
            {
              ...scopeProductsContent[GRIN_SCOPE_TYPE_LG_MINI],
              description: scopeProductsContent[GRIN_SCOPE_TYPE_LG_MINI].selectorDescription,
              price: scopeLGMiniPrice,
              quantity: quantityLGMini,
              maxQuantity: grinKitLGMiniMaxScopeQuantity,
              onSetQuantity: setQuantityLGMini,
              error: formErrors?.quantityLGMini
            }
          ]
        : [])
    ],
    [
      formErrors?.quantity,
      formErrors?.quantityLG,
      formErrors?.quantityLGMini,
      formErrors?.quantityMini,
      grinKitLGMaxScopeQuantity,
      grinKitLGMiniMaxScopeQuantity,
      grinKitMaxScopeQuantity,
      grinKitMiniMaxScopeQuantity,
      quantity,
      quantityLG,
      quantityLGMini,
      quantityMini,
      scopeLGMiniPrice,
      scopeLGPrice,
      scopeMiniPreviousPrice,
      scopeMiniPrice,
      scopePreviousPrice,
      scopePrice,
      canOrderScopeLG,
      canOrderScopeLGMini,
      scopeProductsContent,
      setQuantity,
      setQuantityLG,
      setQuantityLGMini,
      setQuantityMini
    ]
  )

  const amountStepProducts = useMemo(
    () => getOrderGrinKitsAmountStepProducts({ hasFreeScopes, products }),
    [hasFreeScopes, products]
  )

  const isPrimaryDisabled = useMemo(
    () =>
      isCalculatingTaxes ||
      isSubmittingOrder ||
      !isEmpty(formErrors) ||
      (step === CONFIRMATION_STEP ? !agreedTerms : false),
    [agreedTerms, formErrors, isCalculatingTaxes, isSubmittingOrder, step]
  )

  useEffect(() => {
    setInitialShippingAddress()
  }, [practiceDetails, setInitialShippingAddress])

  useEffect(() => {
    if (
      isOrderOpen &&
      practicePlanGroup &&
      !isLoadingScopeProduct &&
      (!hasLoadedScopeProduct || productScopePlanGroup !== practicePlanGroup || loadedIncludeScopeLG !== includeScopeLG)
    ) {
      dispatch(Actions.fetchGrinScopeProducts({ includeScopeLG, grinPlanGroup: practicePlanGroup }))
    }
  }, [
    dispatch,
    hasLoadedScopeProduct,
    includeScopeLG,
    isLoadingScopeProduct,
    isOrderOpen,
    loadedIncludeScopeLG,
    practicePlanGroup,
    productScopePlanGroup
  ])

  useEffect(() => {
    if (hasLoadedScopeProduct && !canOrderScopeLG && storedQuantityLG !== 0) {
      setQuantityLG(0)
    }
  }, [hasLoadedScopeProduct, canOrderScopeLG, storedQuantityLG, setQuantityLG])

  useEffect(() => {
    if (hasLoadedScopeProduct && !canOrderScopeLGMini && storedQuantityLGMini !== 0) {
      setQuantityLGMini(0)
    }
  }, [hasLoadedScopeProduct, canOrderScopeLGMini, storedQuantityLGMini, setQuantityLGMini])

  useEffect(() => {
    if (isOrderOpen && step === CONFIRMATION_STEP) {
      calculateOrderTax()
    }
  }, [isOrderOpen, mappedShippingAddress, calculateOrderTax, step])

  useEffect(() => {
    if (isOrderOpen && isMobile()) {
      const draftOrder = getKistOrder()
      if (draftOrder) {
        setQuantity(draftOrder.quantity)
        setQuantityMini(draftOrder.quantityMini)
        setQuantityLG(draftOrder.quantityLG || 0)
        setQuantityLGMini(draftOrder.quantityLGMini || 0)
      }
    }
  }, [setQuantity, setQuantityLG, setQuantityLGMini, setQuantityMini, isOrderOpen])

  useEffect(() => {
    if (!isOrderOpen) {
      setStep(AMOUNT_STEP)
    }
  }, [isOrderOpen, resetFields])

  return isOrderOpen ? (
    showProductComparison ? (
      <BaseModal
        open={showProductComparison}
        primaryLabel={t('general.back')}
        className={classes.comparisonBody}
        withCloseIcon={false}
        onPrimaryBtnClick={() => setShowProductComparison(false)}
      >
        <OrderGrinKitsComparison />
      </BaseModal>
    ) : (
      <BaseModal
        open={isOrderOpen}
        title={<TrademarkText text={steps[step].title} />}
        secondaryLabel={steps[step].secondaryLabel}
        primaryLabel={steps[step].primaryLabel}
        className={step === CONFIRMATION_STEP ? classes.confirmationStepBody : classes.body}
        contentClassName={isMobile() ? classes.contentBody : step === AMOUNT_STEP ? classes.amountContent : ''}
        titleContainerClassName={isMobile() ? classes.modalTitle : ''}
        titleClassName={!isMobile() && step === AMOUNT_STEP ? classes.amountTitle : ''}
        actionsClassName={isMobile() ? classes.modalActions : step === AMOUNT_STEP ? classes.amountActions : ''}
        isLoading={isSubmittingOrder}
        handleClose={handleClose}
        onSecondaryBtnClick={steps[step].handleSecondary}
        onPrimaryBtnClick={steps[step].handleSubmit}
        isPrimaryDisabled={isPrimaryDisabled}
        primaryType="submit"
        formId="credit-card-form"
        variant={isMobile() ? 'fullscreen' : 'primary'}
        largerButtons={isMobile()}
      >
        {step === AMOUNT_STEP && (
          <OrderGrinKitsAmountContainer
            products={amountStepProducts}
            shippingFee={isNewShippingFee ? newShippingFee?.price || 0 : shippingFee || 0}
            formErrors={formErrors}
            onQuantityChange={onQuantityChange}
            onShowProductComparison={() => setShowProductComparison(true)}
          />
        )}
        {step === CONFIRMATION_STEP && (
          <OrderGrinKitsCheckout
            kitPrice={productScope?.price || 0}
            kitMiniPrice={productScopeMini?.price || 0}
            kitLGPrice={productScopeLG?.price || 0}
            kitLGMiniPrice={productScopeLGMini?.price || 0}
            shippingFee={isNewShippingFee ? newShippingFee?.price || 0 : shippingFee || 0}
            taxes={taxes}
            coupon={coupon}
            quantity={quantity}
            quantityMini={quantityMini}
            quantityLG={quantityLG}
            quantityLGMini={quantityLGMini}
            currency={currency}
            shippingAddress={shippingAddress}
            agreedTerms={agreedTerms}
            shouldShowErrors={shouldShowErrors}
            setAgreedTerms={setAgreedTerms}
            onChangeShippingAddress={handleShippingAddressChange}
            resetShippingAddress={setInitialShippingAddress}
            isLoading={isCalculatingTaxes || isLoadingScopeProduct}
            onCardSaved={handlePayment}
            onResetCoupon={resetCoupon}
            totalDiscount={totalDiscount}
          />
        )}
      </BaseModal>
    )
  ) : null
}

export default OrderGrinKitsModal
